import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AreaScanSummary,
  DeleteImpact,
  LidarScan,
  ScanJob,
  SystemLocation,
  SbcApiService,
} from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import { SCAN_ELIGIBLE_TYPES } from './lidar-location.util';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-location-lidar-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, ConfirmDialogComponent],
  templateUrl: './location-lidar-panel.component.html',
  styleUrl: './location-lidar-panel.component.css',
})
export class LocationLidarPanelComponent {
  private readonly api = inject(SbcApiService);
  private readonly router = inject(Router);
  readonly session = inject(SessionService);

  @Input({ required: true }) locationId!: number;
  @Input() locations: SystemLocation[] = [];
  @Input() embedded = false;
  @Input() showHeader = true;
  @Output() toastMessage = new EventEmitter<string>();
  @Output() scansChanged = new EventEmitter<void>();

  readonly detailLoading = signal(false);
  readonly saving = signal(false);
  readonly detail = signal<SystemLocation | null>(null);
  readonly internalLocations = signal<SystemLocation[]>([]);

  readonly uploadScanOpen = signal(false);
  readonly removingScanJobId = signal<number | null>(null);
  readonly deleteImpactLoadingId = signal<number | null>(null);
  readonly deleteConfirmOpen = signal(false);
  readonly deleteConfirmMessage = signal('');
  private readonly pendingDeleteScan = signal<ScanJob | null>(null);
  readonly processLoading = signal(false);
  readonly ruleCheckLoading = signal(false);
  readonly demoLoading = signal(false);
  readonly scanJobs = signal<ScanJob[]>([]);
  readonly selectedScanJobIds = signal<number[]>([]);
  readonly scanSummary = signal<AreaScanSummary | null>(null);
  readonly uploadFormErrors = signal<{ file?: string; form?: string }>({});

  scanUploadForm = {
    scanType: 'drone' as 'drone' | 'vehicle',
    selectedFile: null as File | null,
  };

  readonly canManageLocations = computed(() =>
    ['officer', 'config_admin'].includes(this.session.role())
  );

  readonly resolvedLocations = computed(() =>
    this.locations.length ? this.locations : this.internalLocations()
  );

  readonly hasUploadedScans = computed(() => this.scanJobs().length > 0);

  constructor() {
    effect(() => {
      this.session.authorityId();
      if (!this.locations.length) {
        this.loadLocations();
      }
    });

    effect(() => {
      const id = this.locationId;
      const locs = this.resolvedLocations();
      if (id == null || !locs.length) return;
      this.loadDetail(id);
    });
  }

  private loadLocations(): void {
    this.api.listSystemLocations({ includeInactive: 'true' }).subscribe({
      next: (res) => this.internalLocations.set(res.data || []),
      error: () => this.internalLocations.set([]),
    });
  }

  loadDetail(id: number): void {
    const loc = this.resolvedLocations().find((l) => l.id === id);
    if (!loc || loc.statusCd !== 'active' || !SCAN_ELIGIBLE_TYPES.has(loc.locationTypeCode)) {
      this.detail.set(null);
      this.scanJobs.set([]);
      this.scanSummary.set(null);
      return;
    }

    this.detailLoading.set(true);
    this.api.getSystemLocation(id).subscribe({
      next: (res) => {
        this.detail.set(res.data);
        this.detailLoading.set(false);
        this.loadScanJobs(id);
        this.loadScanSummary(id);
      },
      error: () => {
        this.detail.set(null);
        this.detailLoading.set(false);
        this.scanJobs.set([]);
        this.scanSummary.set(null);
      },
    });
  }

  reload(): void {
    this.loadDetail(this.locationId);
  }

  loadScanSummary(locationId: number): void {
    this.api.listLocationScanProcessingRuns(locationId, 1).subscribe({
      next: (res) => {
        const run = (res.data || [])[0];
        this.scanSummary.set(run?.areaScanSummary ?? null);
      },
      error: () => this.scanSummary.set(null),
    });
  }

  loadScanJobs(locationId: number): void {
    this.api.listLocationScanJobs(locationId).subscribe({
      next: (res) => {
        this.scanJobs.set(res.data || []);
        this.selectedScanJobIds.set(
          (res.data || [])
            .filter((j) => j.statusCd === 'uploaded' || j.statusCd === 'ready')
            .map((j) => j.id)
        );
      },
      error: () => this.scanJobs.set([]),
    });
  }

  openUploadScan(scanType: 'drone' | 'vehicle' = 'drone'): void {
    if (this.embedded) return;
    this.scanUploadForm = { scanType, selectedFile: null };
    this.clearUploadFormErrors();
    this.uploadScanOpen.set(true);
  }

  triggerEmbeddedUpload(scanType: 'drone' | 'vehicle', input: HTMLInputElement): void {
    this.scanUploadForm.scanType = scanType;
    input.value = '';
    input.click();
  }

  onEmbeddedFileSelected(event: Event, scanType: 'drone' | 'vehicle'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) return;
    this.scanUploadForm = { scanType, selectedFile: file };
    this.submitUploadScan();
    input.value = '';
  }

  closeUploadScan(): void {
    this.uploadScanOpen.set(false);
    this.clearUploadFormErrors();
  }

  onScanFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.scanUploadForm.selectedFile = file;
    if (file) {
      this.uploadFormErrors.update((e) => ({ ...e, file: undefined, form: undefined }));
    }
  }

  submitUploadScan(): void {
    const loc = this.detail();
    const file = this.scanUploadForm.selectedFile;
    if (!loc) {
      this.uploadFormErrors.set({ form: 'Location not loaded. Refresh and try again.' });
      return;
    }
    if (!file) {
      this.uploadFormErrors.set({
        file: 'Choose a LAS or LAZ file before uploading.',
        form: 'Fix the highlighted fields to continue.',
      });
      return;
    }

    this.clearUploadFormErrors();

    const fd = new FormData();
    fd.append('file', file);
    fd.append('scanType', this.scanUploadForm.scanType);
    fd.append('fileFormat', (file.name.split('.').pop() || 'LAZ').toUpperCase());

    this.saving.set(true);
    this.api.uploadLocationScanJob(loc.id, fd).subscribe({
      next: () => {
        this.saving.set(false);
        this.uploadScanOpen.set(false);
        this.emitToast(`${this.scanUploadForm.scanType} scan uploaded`);
        this.loadDetail(loc.id);
        this.scansChanged.emit();
      },
      error: (err) => {
        this.saving.set(false);
        this.uploadFormErrors.set({
          form: err?.error?.message || 'Upload failed. Try again.',
        });
      },
    });
  }

  private clearUploadFormErrors(): void {
    this.uploadFormErrors.set({});
  }

  canRemoveScan(job: ScanJob): boolean {
    return !['processing'].includes(String(job.statusCd || '').toLowerCase());
  }

  removeScanJob(job: ScanJob): void {
    const loc = this.detail();
    if (!loc || !this.canRemoveScan(job)) return;
    this.deleteImpactLoadingId.set(job.id);
    this.api.getScanDeleteImpact(job.id).subscribe({
      next: (res) => {
        this.deleteImpactLoadingId.set(null);
        this.pendingDeleteScan.set(job);
        this.deleteConfirmMessage.set(this.scanDeleteMessage(job, res.data));
        this.deleteConfirmOpen.set(true);
      },
      error: (err) => {
        this.deleteImpactLoadingId.set(null);
        this.emitToast(err?.error?.message || 'Unable to check scan dependencies');
      },
    });
  }

  confirmRemoveScanJob(): void {
    const loc = this.detail();
    const job = this.pendingDeleteScan();
    if (!loc || !job || this.removingScanJobId() != null) return;
    this.deleteConfirmOpen.set(false);
    this.removingScanJobId.set(job.id);
    this.api.deleteScanJob(job.id).subscribe({
      next: () => {
        this.removingScanJobId.set(null);
        this.pendingDeleteScan.set(null);
        this.emitToast(`${job.scanType} scan removed`);
        this.loadDetail(loc.id);
        this.scansChanged.emit();
      },
      error: (err) => {
        this.removingScanJobId.set(null);
        this.emitToast(err?.error?.message || 'Failed to remove scan');
      },
    });
  }

  cancelRemoveScanJob(): void {
    this.deleteConfirmOpen.set(false);
    this.pendingDeleteScan.set(null);
  }

  private scanDeleteMessage(job: ScanJob, impact: DeleteImpact): string {
    const name = this.fileNameFromUri(job.rawStorageUri);
    if (!impact.hasDerivedData) {
      return `Delete ${name}? This action cannot be undone.`;
    }
    return `Delete ${name}? Warning: this scan has already been processed (${impact.processingRunCount || 0} run(s), ${impact.measurementCount} measurement(s), ${impact.flagCount} flag(s)). Related data will be retained for audit, but reprocessing from this file will no longer be possible.`;
  }

  toggleScanJob(id: number): void {
    const cur = this.selectedScanJobIds();
    this.selectedScanJobIds.set(
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  }

  scanJobSelected(id: number): boolean {
    return this.selectedScanJobIds().includes(id);
  }

  canProcessScans(): boolean {
    const jobs = this.scanJobs();
    const selected = this.selectedScanJobIds();
    if (selected.length < 2) return false;
    const types = new Set(
      jobs.filter((j) => selected.includes(j.id)).map((j) => j.scanType)
    );
    return types.has('drone') && types.has('vehicle');
  }

  loadDemoLidar(): void {
    const loc = this.detail();
    if (!loc || this.demoLoading()) return;

    this.demoLoading.set(true);
    this.api.ingestLocationDemoLidar(loc.id, { autoProcess: true }).subscribe({
      next: (res) => {
        this.demoLoading.set(false);
        const meas = res.meta?.['measurement'] as AreaScanSummary & {
          matchedCount?: number;
          buildingsDetected?: number;
          propertyCount?: number;
          mode?: string;
        } | undefined;
        if (meas?.statusCd === 'succeeded') {
          const summary: AreaScanSummary = {
            mode: meas.mode,
            registeredPlotCount: meas.registeredPlotCount,
            buildingsDetected: meas.buildingsDetected,
            matchedCount: meas.matchedCount ?? meas.propertyCount,
            unmatchedBuildings: meas.unmatchedBuildings,
            unmatchedPlots: meas.unmatchedPlots,
            missingBoundaries: meas.missingBoundaries,
            statusCd: meas.statusCd,
            note: meas.note,
            processedAt: new Date().toISOString(),
          };
          this.scanSummary.set(summary);
          this.emitToast(this.buildScanToast(summary, 'Reference LiDAR loaded'));
        } else {
          this.emitToast('Reference LiDAR registered — review scan status');
        }
        this.loadDetail(loc.id);
        this.scansChanged.emit();
      },
      error: (err) => {
        this.demoLoading.set(false);
        this.emitToast(err?.error?.message || 'Reference LiDAR load failed');
      },
    });
  }

  processSelectedScans(): void {
    const loc = this.detail();
    if (!loc || !this.canProcessScans()) {
      this.emitToast('Select one drone and one vehicle scan job to process');
      return;
    }

    this.processLoading.set(true);
    this.api
      .processLocationScans(loc.id, { inputScanJobIds: this.selectedScanJobIds() })
      .subscribe({
        next: (res) => {
          this.processLoading.set(false);
          const meas = res.meta?.['measurement'] as AreaScanSummary & {
            propertyCount?: number;
            mode?: string;
            reason?: string;
          } | undefined;
          if (meas?.statusCd === 'succeeded' && meas.mode === 'area_spatial_match') {
            const summary: AreaScanSummary = {
              mode: meas.mode,
              registeredPlotCount: meas.registeredPlotCount,
              buildingsDetected: meas.buildingsDetected,
              matchedCount: meas.matchedCount ?? meas.propertyCount,
              unmatchedBuildings: meas.unmatchedBuildings,
              unmatchedPlots: meas.unmatchedPlots,
              missingBoundaries: meas.missingBoundaries,
              statusCd: meas.statusCd,
              note: meas.note,
              processedAt: new Date().toISOString(),
            };
            this.scanSummary.set(summary);
            this.emitToast(this.buildScanToast(summary, 'Scan processed'));
          } else if (meas?.statusCd === 'succeeded') {
            this.emitToast('Processing complete — measurements saved');
          } else {
            this.emitToast(meas?.reason || 'Processing finished — review scan analysis');
          }
          this.loadDetail(loc.id);
          this.scansChanged.emit();
        },
        error: (err) => {
          this.processLoading.set(false);
          const msg =
            err?.error?.message ||
            (err?.status === 409
              ? 'Area already processed — measurements already exist for all plots.'
              : 'Processing failed');
          this.emitToast(msg);
        },
      });
  }

  runRuleCheckForLocation(): void {
    const loc = this.detail();
    if (!loc || this.ruleCheckLoading()) return;

    this.ruleCheckLoading.set(true);
    this.api
      .startRuleCheck({
        locationId: loc.id,
        runLabel: `Location ${loc.code} · ${new Date().toISOString()}`,
      })
      .subscribe({
        next: (res) => {
          this.ruleCheckLoading.set(false);
          const data = res.data as {
            alreadyChecked?: boolean;
            message?: string;
            existingRunId?: number;
            run?: { id: number };
          };
          const meta = res.meta as { alreadyChecked?: boolean; message?: string } | undefined;
          if (data?.alreadyChecked || meta?.alreadyChecked) {
            this.emitToast(
              data?.message ||
                meta?.message ||
                'Bye-law check already completed for these measurements'
            );
            const runId = data?.existingRunId || data?.run?.id;
            if (runId && !this.embedded) {
              void this.router.navigate(['/enforcement/compliance-runs'], {
                queryParams: { highlightRun: runId },
              });
            }
            return;
          }
          this.emitToast('Bye-law rule check completed');
          if (!this.embedded) {
            void this.router.navigate(['/enforcement/compliance-runs']);
          }
        },
        error: (err) => {
          this.ruleCheckLoading.set(false);
          const msg =
            err?.error?.message ||
            (err?.status === 409 ? 'Already checked — see Rule-check history' : 'Rule check failed');
          this.emitToast(msg);
        },
      });
  }

  scanLabel(scan: LidarScan): string {
    const name = scan.attributes?.['name'];
    if (name != null && String(name).trim()) return String(name);
    return `${scan.pointCloudFormat} · ${scan.scanDate}`;
  }

  scanJobForType(type: 'drone' | 'vehicle'): ScanJob | null {
    const jobs = this.scanJobs().filter((j) => j.scanType === type);
    if (!jobs.length) return null;
    return jobs.reduce((latest, job) => (job.id > latest.id ? job : latest));
  }

  fileNameFromUri(uri: string | undefined | null): string {
    if (!uri) return 'No file uploaded';
    const parts = uri.split(/[/\\]/);
    return parts[parts.length - 1] || uri;
  }

  scanStatusClass(status: string): string {
    return `status-${(status || 'UPLOADED').toUpperCase()}`;
  }

  formatScanStatus(status: string | undefined): string {
    const key = (status || 'uploaded').toLowerCase();
    const labels: Record<string, string> = {
      uploaded: 'Uploaded',
      ready: 'Ready',
      processed: 'Processed',
      processing: 'Processing',
      failed: 'Failed',
    };
    return labels[key] ?? status ?? 'Unknown';
  }

  scanScopeNote(loc: SystemLocation): string {
    if (loc.locationTypeCode === 'PLOT') {
      return `${loc.code} · Single-plot scan — upload and process to detect buildings.`;
    }
    return `${loc.code} · ${loc.locationTypeName} scan — upload files, then process to see plots detected from the scan.`;
  }

  hasScanIssues(summary: AreaScanSummary): boolean {
    return (
      (summary.unmatchedBuildings?.length ?? 0) > 0 ||
      (summary.unmatchedPlots?.length ?? 0) > 0 ||
      (summary.missingBoundaries?.length ?? 0) > 0
    );
  }

  matchedPlotCount(summary: AreaScanSummary): number {
    return Number(summary.matchedCount ?? 0);
  }

  registeredPlotCount(summary: AreaScanSummary): number {
    return Number(summary.registeredPlotCount ?? 0);
  }

  isFullPlotMatch(summary: AreaScanSummary): boolean {
    const total = this.registeredPlotCount(summary);
    if (total <= 0) return true;
    return this.matchedPlotCount(summary) >= total;
  }

  matchRatioLabel(summary: AreaScanSummary): string {
    const matched = this.matchedPlotCount(summary);
    const total = this.registeredPlotCount(summary);
    if (total > 0) return `${matched}/${total} plots matched`;
    if (matched > 0) return `${matched} plot(s) matched`;
    return 'No plots matched yet';
  }

  missingPlotRefs(summary: AreaScanSummary): string[] {
    return (summary.unmatchedPlots || [])
      .map((item) => String(item.plotRef || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }

  missingPlotRefsLabel(summary: AreaScanSummary): string {
    return this.missingPlotRefs(summary).join(', ');
  }

  matchBannerClass(summary: AreaScanSummary): string {
    if (this.isFullPlotMatch(summary)) return 'lidar-match-banner--ok';
    if (this.matchedPlotCount(summary) > 0) return 'lidar-match-banner--warn';
    return 'lidar-match-banner--fail';
  }

  matchBannerDetail(summary: AreaScanSummary): string {
    if (this.isFullPlotMatch(summary)) {
      return 'All registered plots in this scan area have LiDAR measurements.';
    }
    const missing = this.missingPlotRefsLabel(summary);
    if (missing) {
      return `Missing: Khasra ${missing} — manually check these plots or re-process LiDAR.`;
    }
    if ((summary.unmatchedBuildings?.length ?? 0) > 0) {
      return 'Some building clusters did not match a registered plot — review unmatched items below.';
    }
    if ((summary.missingBoundaries?.length ?? 0) > 0) {
      return `Plot boundaries missing for: ${summary.missingBoundaries?.join(', ')}`;
    }
    return 'Review scan results before proceeding to measurements compare.';
  }

  private buildScanToast(summary: AreaScanSummary, prefix: string): string {
    const headline = this.matchRatioLabel(summary);
    const missing = this.missingPlotRefsLabel(summary);
    if (missing) {
      return `${prefix} — ${headline}. Missing: ${missing}.`;
    }
    return `${prefix} — ${headline}.`;
  }

  private emitToast(msg: string): void {
    this.toastMessage.emit(msg);
  }
}
